import { useState, useEffect } from 'react';
import { TrendingUp, Users, LogIn, AlertTriangle, Download } from 'lucide-react';
import { Card, CardHeader, Button } from '@/components/common';
import { analyticsService } from '@/services/analyticsService';
import { formatNumber } from '@/utils/format';

export function AnalyticsPage() {
  const [dateRange, setDateRange] = useState('7');
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalLogins: 0,
    failedLogins: 0,
    oauthAuthorizations: 0,
    apiRequests: 0,
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    
    try {
      const data = await analyticsService.getAnalytics();
      setAnalytics({
        totalUsers: data.total_users || 0,
        activeUsers: data.active_users || 0,
        totalLogins: data.total_logins || 0,
        failedLogins: data.failed_logins || 0,
        oauthAuthorizations: data.oauth_authorizations || 0,
        apiRequests: data.api_requests || 0,
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      
    }
  };

  const exportData = () => {
    const csv = `Metric,Value\nTotal Users,${analytics.totalUsers}\nActive Users,${analytics.activeUsers}\nTotal Logins,${analytics.totalLogins}\nFailed Logins,${analytics.failedLogins}\nOAuth Authorizations,${analytics.oauthAuthorizations}\nAPI Requests,${analytics.apiRequests}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const stats = [
    { label: 'Total Users', value: formatNumber(analytics.totalUsers), icon: Users, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Active Users', value: formatNumber(analytics.activeUsers), icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-100' },
    { label: 'Total Logins', value: formatNumber(analytics.totalLogins), icon: LogIn, color: 'text-purple-600', bg: 'bg-purple-100' },
    { label: 'Failed Logins', value: formatNumber(analytics.failedLogins), icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Analytics</h1>
          <p className="text-secondary-600 mt-1">System metrics and usage statistics</p>
        </div>
        <div className="flex gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button variant="outline" icon={<Download className="w-4 h-4" />} onClick={exportData}>
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <Card key={idx}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600">{stat.label}</p>
                <p className="text-2xl font-bold text-secondary-900 mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bg}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="OAuth Authorizations" description="OAuth 2.0 authorization grants" />
          <div className="text-center py-12">
            <p className="text-4xl font-bold text-primary-600">{formatNumber(analytics.oauthAuthorizations)}</p>
            <p className="text-secondary-600 mt-2">Total authorizations</p>
          </div>
        </Card>

        <Card>
          <CardHeader title="API Requests" description="Total API endpoint calls" />
          <div className="text-center py-12">
            <p className="text-4xl font-bold text-primary-600">{formatNumber(analytics.apiRequests)}</p>
            <p className="text-secondary-600 mt-2">Total requests</p>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader title="Security Metrics" description="Authentication and security statistics" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
          <div className="text-center">
            <p className="text-sm text-secondary-600">Success Rate</p>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {analytics.totalLogins > 0 
                ? Math.round((1 - analytics.failedLogins / analytics.totalLogins) * 100)
                : 100}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-secondary-600">Failed Attempts</p>
            <p className="text-3xl font-bold text-red-600 mt-2">{formatNumber(analytics.failedLogins)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-secondary-600">Successful Logins</p>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {formatNumber(analytics.totalLogins - analytics.failedLogins)}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
